import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import http from "@/lib/api";
import { Panel, Table, Td, Empty, fmt } from "./AdminKit";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Trash2, IdCard, Zap } from "lucide-react";
import { STATUS_RU } from "@/lib/statuses";
import { computeSla, slaBadgeClass } from "@/lib/sla";
import AdminRepairCard from "./AdminRepairCard";
import AdminQuickIntake from "./AdminQuickIntake";

const priorityLabel = { akutt: "Акутт", hoy: "Высокий", normal: "Обычный" };
const priorityCls = { akutt: "bg-red-900/40 text-red-300 border-red-700/50", hoy: "bg-amber-900/40 text-amber-300 border-amber-700/50", normal: "bg-slate-700 text-slate-300 border-slate-600" };

export default function AdminRepairOrders() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [cardId, setCardId] = useState(null);
  const [quickOpen, setQuickOpen] = useState(false);

  const load = () => http.get("/v1/admin/repair-orders").then((r) => setRows(r.data)).catch(() => {});
  const [params] = useSearchParams();
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const id = params.get("open");
    if (id) { setCardId(id); setOpen(true); }
  }, [params]);

  const openCard = (id) => { setCardId(id); setOpen(true); };
  const remove = async (id) => { if (!window.confirm("Удалить заказ?")) return; try { await http.delete(`/v1/admin/repair-orders/${id}`); toast.success("Удалено"); load(); } catch { toast.error("Ошибка"); } };

  return (
    <div className="space-y-6" data-testid="admin-repair-orders">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Ремонтные заказы</h1>
        <div className="flex gap-2">
          <Button className="bg-amber-600 hover:bg-amber-700 text-white gap-2" onClick={() => setQuickOpen(true)} data-testid="quick-intake-btn"><Zap className="h-4 w-4" />Быстрый приём</Button>
          <Button className="bg-blue-600 hover:bg-blue-700 text-white gap-2" onClick={() => openCard(null)} data-testid="order-add"><Plus className="h-4 w-4" />Новая карточка</Button>
        </div>
      </div>
      <Panel title={`Всего: ${rows.length} (срочные вверху)`}>
        {rows.length === 0 ? <Empty /> : (
          <Table head={["Приоритет", "Номер", "Клиент", "Машина", "Статус", "SLA", "Стоимость", "Создан", "Действия"]}>
            {rows.map((r) => {
              const sla = computeSla(r);
              return (
              <tr key={r.id} className={`border-b border-slate-700/50 ${r.priority === "akutt" ? "bg-red-950/20" : ""}`}>
                <Td><span className={`inline-block px-2 py-0.5 rounded-full text-xs border ${priorityCls[r.priority] || priorityCls.normal}`}>{priorityLabel[r.priority] || "Обычный"}</span></Td>
                <Td><button onClick={() => openCard(r.id)} className="text-blue-400 hover:text-blue-300 font-medium" data-testid={`order-open-${r.order_number}`}>{r.order_number}</button></Td>
                <Td>{r.customer_name}</Td>
                <Td>{[r.machine_brand, r.machine_model].filter(Boolean).join(" ")}</Td>
                <Td><span className="inline-block px-2 py-0.5 rounded-full text-xs bg-slate-700 text-slate-200 border border-slate-600">{STATUS_RU[r.status] || r.status}</span>{r.customer_declined_offer && <span className="ml-1 inline-block px-2 py-0.5 rounded-full text-xs bg-amber-900/50 text-amber-300 border border-amber-700/60" data-testid={`declined-badge-${r.order_number}`}>Kunde har avslått tilbud</span>}</Td>
                <Td>{sla ? <span className={`inline-block px-2 py-0.5 rounded-full text-xs border ${slaBadgeClass[sla.state]}`} title={`Цель: ${sla.hours}ч (внутр.)`} data-testid={`sla-${r.order_number}`}>{sla.label}</span> : "-"}</Td>
                <Td>{r.estimated_cost || "-"}</Td>
                <Td>{fmt(r.created_at)}</Td>
                <Td>
                  <div className="flex gap-2">
                    <button onClick={() => openCard(r.id)} className="text-blue-400 hover:text-blue-300" title="Открыть карточку"><IdCard className="h-4 w-4" /></button>
                    <button onClick={() => remove(r.id)} className="text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </Td>
              </tr>
              );
            })}
          </Table>
        )}
      </Panel>

      <AdminRepairCard open={open} onOpenChange={setOpen} orderId={cardId} onSaved={load} />
      <AdminQuickIntake open={quickOpen} onOpenChange={setQuickOpen} onCreated={(id, openCardAfter) => { load(); if (openCardAfter) openCard(id); }} />
    </div>
  );
}
